import * as THREE from 'three';
import type { Random } from '../../core/Random';
import { box, cyl, M, mat, mesh, registerNightGlow, sphere, texMat } from '../materials';
import { rustTexture, signTexture, woodTexture } from '../textures';

/**
 * Props for the rough side of the park: burned and condemned trailers, a school bus
 * somebody lives in, a moonshine still, dead trees, burn barrels and the crick.
 */

const charred = (): THREE.MeshStandardMaterial => mat('#2b241f', { rough: 1 });

/** What's left of a trailer after a grease fire. Local frame matches buildTrailer. */
export function burnedShell(rng: Random): THREE.Group {
  const frame = new THREE.Group();
  const c = charred();
  frame.add(box(12, 0.25, 4, c, 0, 0.5, 0));
  for (let x = -5.8; x <= 5.8; x += 1.45) {
    const h = rng.range(0.6, 2.4);
    frame.add(box(0.15, h, 0.15, c, x, 0.6 + h / 2, 1.95));
    if (rng.chance(0.6)) frame.add(box(0.15, h * 0.8, 0.15, c, x, 0.6 + (h * 0.8) / 2, -1.95));
  }
  frame.add(box(4, 1.8, 0.1, c, -3.5, 1.5, -1.95));
  const roofPiece = box(5, 0.1, 3.4, mat('#4a4744', { rough: 0.8 }), 2, 1.1, 0);
  roofPiece.rotation.z = 0.35;
  frame.add(roofPiece);
  return frame;
}

/** Spray paint on a wall: a transparent decal plane. */
export function graffiti(text: string, color: string, w: number, h: number, size = 110): THREE.Mesh {
  const tex = signTexture({
    w: 512,
    h: Math.round((512 * h) / w),
    bg: 'rgba(0,0,0,0)',
    lines: [{ text, size, font: 'Permanent Marker', color }],
  });
  const m = texMat(tex, { transparent: true }).clone();
  m.depthWrite = false;
  m.polygonOffset = true;
  m.polygonOffsetFactor = -2;
  const decal = mesh(new THREE.PlaneGeometry(w, h), m, 0, 0, 0, { cast: false, receive: false });
  decal.userData.noBatch = true;
  return decal;
}

/** Yellow "caution" tape strung between two points. */
export function cautionTape(a: THREE.Vector3, b: THREE.Vector3): THREE.Mesh {
  const len = a.distanceTo(b);
  const tape = box(len, 0.08, 0.01, mat('#f2c200', { rough: 0.6 }), 0, 0, 0, { cast: false });
  tape.position.lerpVectors(a, b, 0.5);
  tape.rotation.y = -Math.atan2(b.z - a.z, b.x - a.x);
  return tape;
}

/** A retired school bus somebody calls home. Long axis = x, door on the +z side. */
export function schoolBus(rng: Random): THREE.Group {
  const g = new THREE.Group();
  const yellow = mat('#e8b023', { rough: 0.75 });
  g.add(box(8.6, 2.2, 2.4, yellow, 0, 1.75, 0));
  g.add(box(1.6, 1.1, 2.3, yellow, 5.05, 1.2, 0));
  // Rust where the paint gave up.
  const rust = texMat(rustTexture(), { color: '#b0703a' });
  for (const [x, y, w, h] of [
    [-3.2, 0.95, 1.6, 0.5],
    [1.4, 1.05, 1.1, 0.35],
    [3.4, 2.55, 0.9, 0.3],
  ] as const)
    for (const s of [-1, 1]) g.add(box(w, h, 0.02, rust, x, y, s * 1.215, { cast: false }));
  g.add(box(8.62, 0.12, 2.42, M.black, 0, 1.55, 0, { cast: false }));
  g.add(box(8.62, 0.12, 2.42, M.black, 0, 2.35, 0, { cast: false }));
  g.add(box(8.7, 0.18, 2.5, mat('#cfc6a8', { rough: 0.9 }), 0, 2.93, 0));
  const glassM = mat('#2b3436', { rough: 0.3, metal: 0.3 });
  for (let i = 0; i < 7; i++) {
    for (const s of [-1, 1]) {
      const pane = box(
        0.9,
        0.62,
        0.04,
        rng.chance(0.25) ? mat('#9c8a6a') : glassM,
        -3.6 + i * 1.12,
        2.0,
        s * 1.21,
      );
      g.add(pane);
    }
  }
  g.add(box(0.05, 0.8, 1.9, glassM, 4.31, 2.05, 0));
  // Flat tires on the ground, the bus resting on cinder blocks.
  for (const x of [-2.8, 3.6])
    for (const s of [-1, 1]) {
      const t = cyl(0.48, 0.48, 0.32, M.rubber, x, 0.32, s * 1.12, 10);
      t.rotation.x = Math.PI / 2;
      t.scale.y = 0.75;
      g.add(t);
      g.add(box(0.45, 0.42, 0.3, M.cinder, x, 0.21, s * 0.75));
    }
  // Stovepipe through the roof, and a blue tarp awning on poles.
  g.add(cyl(0.12, 0.12, 1.4, M.steel, -2.4, 3.6, -0.6, 8));
  g.add(cyl(0.2, 0.2, 0.08, M.steel, -2.4, 4.32, -0.6, 8));
  const tarp = box(4.2, 0.04, 2.6, mat('#1f5fbf', { rough: 0.6 }), -0.6, 2.75, 2.5);
  tarp.rotation.x = 0.22;
  g.add(tarp);
  for (const x of [-2.6, 1.4]) g.add(cyl(0.05, 0.05, 2.4, M.pole, x, 1.2, 3.7, 6));
  const door = box(0.9, 1.9, 0.05, mat('#3a3a3a'), 3.6, 1.65, 1.22);
  g.add(door);
  return g;
}

/** Wooden outhouse with the crescent moon. Door on +z. */
export function outhouse(): THREE.Group {
  const g = new THREE.Group();
  const wood = texMat(woodTexture(), { color: '#9c7a52' });
  g.add(box(1.2, 2.1, 1.2, wood, 0, 1.05, 0));
  const roof = box(1.5, 0.08, 1.6, mat('#5a5148', { metal: 0.3 }), 0, 2.25, 0);
  roof.rotation.x = -0.18;
  g.add(roof);
  g.add(box(0.8, 1.8, 0.05, texMat(woodTexture(), { color: '#86653f' }), 0, 0.98, 0.61));
  const moon = new THREE.Shape();
  moon.absarc(0, 0, 0.12, Math.PI * 0.3, Math.PI * 1.7, false);
  moon.absarc(0.06, 0, 0.1, Math.PI * 1.6, Math.PI * 0.4, true);
  const cut = mesh(new THREE.ShapeGeometry(moon), M.black, 0.05, 1.6, 0.64, { cast: false });
  g.add(cut);
  return g;
}

export interface StillModel {
  group: THREE.Group;
  /** Flicker this at night (emissive coals under the pot). */
  fire: THREE.MeshStandardMaterial;
}

/** Uncle Rudy's copper pot still, hidden in the woods. */
export function moonshineStill(rng: Random): StillModel {
  const g = new THREE.Group();
  const copper = mat('#b87333', { rough: 0.35, metal: 0.85 });
  // Fire ring and coals.
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    g.add(box(0.28, 0.24, 0.22, M.cinder, Math.cos(a) * 0.62, 0.12, Math.sin(a) * 0.62));
  }
  const fire = new THREE.MeshStandardMaterial({
    color: '#3a1a0a',
    emissive: new THREE.Color('#ff6a1a'),
    emissiveIntensity: 1.4,
  });
  const coals = mesh(new THREE.CylinderGeometry(0.5, 0.55, 0.12, 10), fire, 0, 0.08, 0, { cast: false });
  coals.userData.noBatch = true;
  g.add(coals);
  // The pot, its cap and the line to the thump keg and the worm barrel.
  g.add(cyl(0.6, 0.55, 1.0, copper, 0, 0.75, 0, 14));
  const dome = sphere(0.6, copper, 0, 1.25, 0, 14);
  dome.scale.set(1, 0.55, 1);
  g.add(dome);
  g.add(cyl(0.12, 0.2, 0.5, copper, 0, 1.75, 0, 10));
  const arm = cyl(0.06, 0.06, 1.7, copper, 0.85, 1.95, 0, 6);
  arm.rotation.z = Math.PI / 2 - 0.25;
  g.add(arm);
  const barrelM = texMat(woodTexture(), { color: '#7a5a3a' });
  g.add(cyl(0.4, 0.4, 0.95, barrelM, 1.75, 0.48, 0, 12));
  g.add(cyl(0.45, 0.45, 1.15, barrelM, 2.9, 0.58, 0.2, 12));
  for (let i = 0; i < 4; i++) {
    const coil = mesh(new THREE.TorusGeometry(0.28, 0.035, 6, 14), copper, 2.9, 0.4 + i * 0.22, 0.2);
    coil.rotation.x = Math.PI / 2;
    g.add(coil);
  }
  // Mason jars, full and empty, on a crate.
  g.add(box(0.8, 0.45, 0.5, texMat(woodTexture(), { color: '#a58a5c' }), 3.1, 0.23, -0.9));
  const jar = mat('#d8e8ee', { rough: 0.1, metal: 0.1, transparent: true, opacity: 0.55 });
  const shine = mat('#f4f1e8', { rough: 0.1, transparent: true, opacity: 0.7 });
  for (let i = 0; i < 5; i++) {
    const x = 2.85 + (i % 3) * 0.22;
    const z = -1.0 + Math.floor(i / 3) * 0.22;
    g.add(cyl(0.08, 0.08, 0.22, i < 3 ? shine : jar, x, 0.57, z, 8));
    g.add(cyl(0.085, 0.085, 0.04, M.steel, x, 0.69, z, 8));
  }
  // Sugar sacks and a wood pile.
  for (let i = 0; i < 3; i++) {
    const sack = box(0.5, 0.3, 0.75, mat('#e6dcc4'), -1.6 + rng.range(-0.1, 0.1), 0.15 + i * 0.28, -0.9);
    sack.rotation.y = rng.range(-0.3, 0.3);
    g.add(sack);
  }
  for (let i = 0; i < 6; i++) {
    const log = cyl(0.09, 0.09, 1.0, M.trunk, -1.4, 0.1 + Math.floor(i / 3) * 0.18, 0.6 + (i % 3) * 0.2, 6);
    log.rotation.z = Math.PI / 2;
    g.add(log);
  }
  return { group: g, fire };
}

/** Bare, gray, and done with all of this. */
export function deadTree(rng: Random): THREE.Group {
  const g = new THREE.Group();
  const s = rng.range(0.8, 1.4);
  const bark = mat(rng.pick(['#4d4339', '#3f3832', '#57493d']), { rough: 1, flat: true });
  const h = 4.5 * s;
  g.add(cyl(0.08 * s, 0.24 * s, h, bark, 0, h / 2, 0, 6));
  const n = rng.int(3, 6);
  for (let i = 0; i < n; i++) {
    const len = rng.range(1, 2.2) * s;
    const branch = cyl(0.03 * s, 0.08 * s, len, bark, 0, 0, 0, 5);
    const y = rng.range(h * 0.45, h * 0.9);
    const a = rng.range(0, Math.PI * 2);
    const tilt = rng.range(0.5, 1.1);
    branch.position.set(
      Math.cos(a) * Math.sin(tilt) * len * 0.5,
      y + Math.cos(tilt) * len * 0.5,
      Math.sin(a) * Math.sin(tilt) * len * 0.5,
    );
    branch.rotation.set(Math.sin(a) * tilt, 0, -Math.cos(a) * tilt);
    g.add(branch);
  }
  return g;
}

/** A rusty 55-gallon drum with something burning in it. */
export function burnBarrel(): { group: THREE.Group; flame: THREE.MeshStandardMaterial } {
  const g = new THREE.Group();
  g.add(cyl(0.32, 0.32, 0.9, texMat(rustTexture(), { color: '#8a5a3a' }), 0, 0.45, 0, 12));
  const flame = new THREE.MeshStandardMaterial({
    color: '#ff9a3a',
    emissive: new THREE.Color('#ff7a1a'),
    emissiveIntensity: 1.8,
    transparent: true,
    opacity: 0.9,
  });
  const f = mesh(new THREE.ConeGeometry(0.24, 0.55, 7), flame, 0, 1.15, 0, { cast: false });
  f.userData.noBatch = true;
  g.add(f);
  return { group: g, flame };
}

/** A pile of tires nobody will ever deal with. */
export function tireMountain(rng: Random, n: number, radius: number): THREE.Group {
  const g = new THREE.Group();
  for (let i = 0; i < n; i++) {
    const a = rng.range(0, Math.PI * 2);
    const r = rng.range(0, radius) * (1 - i / (n * 1.6));
    const t = mesh(
      new THREE.TorusGeometry(0.34, 0.13, 6, 12),
      M.rubber,
      Math.cos(a) * r,
      0.13 + (i / n) * radius * 0.9 + rng.range(-0.1, 0.1),
      Math.sin(a) * r,
    );
    t.rotation.set(Math.PI / 2 + rng.range(-0.5, 0.5), rng.range(0, 3), rng.range(-0.4, 0.4));
    g.add(t);
  }
  return g;
}

/** Plank bridge over the crick, running along z. */
export function plankBridge(length: number, width: number): THREE.Group {
  const g = new THREE.Group();
  const wood = texMat(woodTexture(), { color: '#8a6a48' });
  for (let z = -length / 2; z <= length / 2; z += 0.42) {
    const plank = box(width, 0.08, 0.36, wood, 0, 0.22, z);
    plank.rotation.y = Math.sin(z * 7.3) * 0.03;
    g.add(plank);
  }
  for (const s of [-1, 1]) {
    g.add(box(0.14, 0.14, length + 0.6, M.trunk, s * (width / 2 - 0.1), 0.12, 0));
    g.add(box(0.08, 0.08, length, M.trunk, s * (width / 2 + 0.05), 0.95, 0));
    for (let z = -length / 2; z <= length / 2 + 0.01; z += length / 3)
      g.add(box(0.1, 0.9, 0.1, M.trunk, s * (width / 2 + 0.05), 0.5, z));
  }
  return g;
}

/** A shopping cart, tipped over like it was shot. */
export function shoppingCart(): THREE.Group {
  const cart = new THREE.Group();
  cart.add(box(0.9, 0.6, 0.55, mat('#b8bcc0', { metal: 0.6 }), 0, 0.75, 0));
  cart.add(box(0.06, 0.06, 0.6, M.chrome, -0.5, 1.1, 0));
  return cart;
}

/** A cornhole board: 2 ft x 4 ft, sloped, hole near the top. Long axis = x (top at +x). */
export function cornholeBoard(paint: string): THREE.Group {
  const g = new THREE.Group();
  const wood = texMat(woodTexture(), { color: '#d8b98a' });
  const top = new THREE.Group();
  top.add(box(1.22, 0.04, 0.61, wood, 0, 0, 0));
  top.add(box(1.0, 0.045, 0.2, mat(paint), -0.05, 0.002, 0, { cast: false }));
  top.add(
    mesh(new THREE.CircleGeometry(0.075, 14), M.black, 0.36, 0.03, 0, { cast: false }).rotateX(-Math.PI / 2),
  );
  top.position.set(0, 0.2, 0);
  top.rotation.z = Math.atan2(0.3, 1.22);
  g.add(top);
  for (const z of [-0.27, 0.27]) g.add(box(0.05, 0.38, 0.05, wood, 0.55, 0.19, z));
  return g;
}

/** A corn-filled beanbag (they're never filled with corn anymore). */
export function cornholeBag(color: string): THREE.Mesh {
  const b = box(0.15, 0.04, 0.15, mat(color, { rough: 0.9 }), 0, 0.02, 0);
  b.userData.noBatch = true;
  return b;
}

export function picnicTable(): THREE.Group {
  const g = new THREE.Group();
  const wood = texMat(woodTexture(), { color: '#a57c52' });
  g.add(box(2.2, 0.06, 0.85, wood, 0, 0.78, 0));
  for (const z of [-0.75, 0.75]) g.add(box(2.2, 0.05, 0.3, wood, 0, 0.45, z));
  for (const x of [-0.85, 0.85])
    for (const s of [-1, 1]) {
      const leg = box(0.08, 0.95, 0.08, wood, x, 0.42, s * 0.42);
      leg.rotation.x = s * 0.55;
      g.add(leg);
    }
  return g;
}

/** Power and water hookups for an empty lot. */
export function utilityPedestal(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.3, 1.1, 0.25, mat('#9aa0a6', { metal: 0.4, rough: 0.5 }), 0, 0.55, 0));
  g.add(cyl(0.09, 0.09, 0.05, M.white, 0, 0.8, 0.14, 10).rotateX(Math.PI / 2));
  g.add(cyl(0.03, 0.03, 0.35, mat('#2f6fbf'), 0.22, 0.18, 0, 6));
  return g;
}

/** A lamp head that flickers (driven by the world's animators). */
export function flickerLamp(): { group: THREE.Group; bulb: THREE.MeshStandardMaterial } {
  const g = new THREE.Group();
  g.add(cyl(0.08, 0.11, 6, texMat(rustTexture(), { color: '#6a5a4a' }), 0, 3, 0, 8));
  g.add(box(0.1, 0.1, 1.4, M.steel, 0, 5.93, 0.65));
  g.add(box(0.36, 0.14, 0.5, mat('#3a3d40'), 0, 5.88, 1.3));
  const bulb = new THREE.MeshStandardMaterial({ color: '#d9d2b0', emissive: new THREE.Color('#d7f0a0') });
  registerNightGlow(bulb, 1.6);
  const b = mesh(new THREE.BoxGeometry(0.26, 0.04, 0.38), bulb, 0, 5.8, 1.3, { cast: false });
  b.userData.noBatch = true;
  g.add(b);
  return { group: g, bulb };
}

/** Cattails and reeds along a bank: instanced, so a few hundred cost one draw call. */
export function reeds(rng: Random, points: THREE.Vector3[]): THREE.InstancedMesh {
  const geo = new THREE.ConeGeometry(0.05, 1.3, 4);
  geo.translate(0, 0.65, 0);
  const inst = new THREE.InstancedMesh(geo, mat('#6b7a3a', { flat: true }), points.length);
  const m4 = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  points.forEach((p, i) => {
    const s = rng.range(0.6, 1.3);
    q.setFromEuler(new THREE.Euler(rng.range(-0.25, 0.25), 0, rng.range(-0.25, 0.25)));
    m4.compose(p, q, new THREE.Vector3(s, s, s));
    inst.setMatrixAt(i, m4);
  });
  inst.castShadow = false;
  inst.receiveShadow = true;
  return inst;
}

/** The crick: a muddy ribbon of water following `centerZ(x)`. */
export function creekWater(
  minX: number,
  maxX: number,
  width: number,
  centerZ: (x: number) => number,
): THREE.Group {
  const g = new THREE.Group();
  const strip = (w: number, y: number, material: THREE.Material): THREE.Mesh => {
    const segs = Math.ceil((maxX - minX) / 1.5);
    const pos: number[] = [];
    const idx: number[] = [];
    for (let i = 0; i <= segs; i++) {
      const x = minX + ((maxX - minX) * i) / segs;
      const z = centerZ(x);
      pos.push(x, y, z - w / 2, x, y, z + w / 2);
      if (i < segs) {
        const a = i * 2;
        idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    const m = new THREE.Mesh(geo, material);
    m.receiveShadow = true;
    return m;
  };
  g.add(strip(width + 2.2, 0.025, mat('#4a3a28', { rough: 1 })));
  g.add(strip(width, 0.045, mat('#5b6b3c', { rough: 0.08, metal: 0.35 })));
  return g;
}
